"use client";

import { Fragment, useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import dynamic from "next/dynamic";
import { useLocale, useT } from "@/lib/locale-context";
import { Building2, House, Loader2, MapPin, Pencil, Route, Search, TrainFront, X } from "lucide-react";
import { fetchAPI, getComplexes } from "@/lib/api";
import { isCoordinateLabel, labelOfNearest, nearestPlace, type NearestPlace } from "@/lib/place-label";
import {
  type FieldOption,
  type OurPlace,
  type PhotonFeature,
  type PlaceSource,
  type ResidenceEntry,
  type Resolved,
  houseFromPhoton,
  houseQuery,
  matchResidences,
  optionGroup,
  orderOptions,
  photonWanted,
  placeKind,
  resolvedText,
  splitHouseNumber,
} from "@/lib/calc-place";

// Lazy-load the map - keeps the ~150 KB mapbox-gl bundle out of pages
// that don't need it. ``ssr: false`` is required because mapbox-gl
// touches ``window`` at module load time.
//
// The loader takes the FULL parent size (h-full + w-full) and shows a
// pulsing gray block - the parent slot already reserves the right
// height (modal: ``min-h-[60vh] sm:min-h-[520px]``; inline preview:
// ``h-72`` via the LeafletMap default className), so the loader and
// loaded map have matching dimensions and no content shift occurs.
const LeafletMap = dynamic(() => import("./LeafletMapMapbox"), {
  ssr: false,
  loading: () => (
    <div className="h-full w-full bg-gray-100 animate-pulse flex items-center justify-center text-[11px] text-gray-400">
      <MapPin className="w-4 h-4 mr-1.5 opacity-60" />
      Loading map…
    </div>
  ),
});

// ── Photon (komoot-hosted OSM geocoder) ────────────────────────────
//
// Free, no API key, CORS-enabled autocomplete over OpenStreetMap.
// Constrained to a Baku bounding box so results don't drift to Baku,
// TX or "Baku Street" anywhere else in the world. Mapbox Geocoding +
// Search Box were both evaluated during Phase 1.5 of the Mapbox
// migration and had near-zero coverage of Baku streets ("Nizami
// küçəsi" → 0 results); the paid Mapbox Boost-1 dataset that fixes
// this is $400+/mo. Photon stays as the address geocoder; Mapbox
// only handles map rendering.
const PHOTON_URL = "https://photon.komoot.io/api/";

/** Static-image preview in compact mode; the GL map only loads in the modal. */
const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_TOKEN;
const BAKU_BBOX = "49.55,40.20,50.65,40.70";

async function searchAddresses(
  q: string,
  signal: AbortSignal,
  limit = 6,
): Promise<PhotonFeature[]> {
  if (q.length < 2) return [];
  const url =
    `${PHOTON_URL}?q=${encodeURIComponent(q)}&bbox=${BAKU_BBOX}&limit=${limit}`;
  const res = await fetch(url, { signal });
  if (!res.ok) return [];
  const data = await res.json();
  const features: PhotonFeature[] = Array.isArray(data?.features)
    ? data.features
    : [];
  return features.filter((f) => formatAddressLabel(f.properties).length > 0);
}

// Reverse-geocode a single point to the nearest OSM address via the
// same Photon instance. Used by the modal footer so the user sees a
// human-readable label ("Nizami küçəsi 12, Nəsimi") rather than
// "40.3811, 49.8501". Falls back to null when Photon returns nothing
// or the network fails; the caller already has a lat/lng fallback.
async function reverseGeocode(
  lat: number,
  lng: number,
  signal: AbortSignal,
): Promise<string | null> {
  const url = `https://photon.komoot.io/reverse?lat=${lat}&lon=${lng}`;
  const res = await fetch(url, { signal });
  if (!res.ok) return null;
  const data = await res.json();
  const features: PhotonFeature[] = Array.isArray(data?.features)
    ? data.features
    : [];
  if (features.length === 0) return null;
  const label = formatAddressLabel(features[0].properties);
  if (!label) return null;
  const subtitle = addressSubtitle(features[0].properties);
  return subtitle ? `${label}, ${subtitle}` : label;
}

function formatAddressLabel(p: PhotonFeature["properties"]): string {
  const street = p.street || p.name;
  if (!street) return p.name || "";
  return p.housenumber ? `${street} ${p.housenumber}` : street;
}

function addressSubtitle(p: PhotonFeature["properties"]): string {
  return [p.district, p.city || "Bakı"].filter(Boolean).join(", ");
}

export interface SelectedLocation {
  latitude: number;
  longitude: number;
  /** The area in our vocabulary: the residence's district, a place's
   *  name, a street's district; null for a map pin or a Photon address. */
  district: string | null;
  /** Set when picked from the typeahead - used for fact-check linkage */
  complex_korter_slug: string | null;
  /** Display label: complex name, address, or "lat, lng" for raw map pins */
  display_label: string;
  /** How the point was obtained; the calculator sends it as
   *  location_source (backend services/calc_place decides from it). */
  source?: PlaceSource;
  /** streets.id of a street or house-address pick. */
  street_id?: number | null;
  /** bina_location id (or "x-<slug>") of a place pick. */
  place_id?: string | null;
  /** Catalogue cluster id of a residence pick (or the residence a pin
   *  stands on). */
  complex_slug?: string | null;
  /** What the valuation will use, the quiet line under the input. */
  resolved?: Resolved | null;
  /** A typed house number that could not be placed: the street's
   *  middle is used, and the field says so. */
  number_missed?: string | null;
}

interface Props {
  value: SelectedLocation | null;
  onChange: (loc: SelectedLocation | null) => void;
  // Compact mode collapses the tall "pick on map" placeholder into a
  // small map button tucked inside the search input - keeps the field
  // a single row. Used on the tax calculator where vertical space is
  // tight; the seller flow keeps the full-height preview/placeholder.
  compact?: boolean;
  /** Compact mode only: a taller preview that is always on screen - an
   *  empty dashed box before anything is picked, the static map after.
   *  Used by the two-column valuation form, where the map is the right
   *  half of the card and must not appear out of nowhere. */
  fillPreview?: boolean;
  /** The valuation calculator (owner, 2026-10-26, analysis 1800): our
   *  places (metro, settlement, rayon, landmark) and streets first, a
   *  typed house number kept (Photon places the house), Photon only for
   *  such an address or when nothing of ours matches, and the place the
   *  valuation will use shown under the input. Other forms keep the
   *  residences + Photon search they had. */
  valuation?: boolean;
}

const BAKU_CENTER: [number, number] = [40.4093, 49.8671];

const sameNumber = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? "").replace(/\s+/g, "").toUpperCase() === (b ?? "").replace(/\s+/g, "").toUpperCase();

/** The quiet line for a point named by the nearest endpoint: a pin
 *  (the residence it stands on, else its rayon and the nearest place),
 *  or an address (its own label with the rayon). */
function resolvedFromNearest(loc: SelectedLocation, n: NearestPlace): Resolved {
  const rayon = n.rayon ?? (n.location_type === "rayon" ? n.location_name : null);
  if (loc.source === "address" || loc.source === "street") {
    return { kind: loc.source, name: loc.display_label, area: rayon };
  }
  if (n.complex_name) return { kind: "pin", name: n.complex_name, area: rayon, onResidence: true };
  return { kind: "pin", name: n.location_type === "rayon" ? "" : (n.location_name ?? ""), area: rayon };
}

/**
 * Address-typeahead + modal map for the seller flow.
 *
 * Search box does typeahead over ~140 mapped residential complexes
 * AND live OSM addresses via Photon (grouped under separate headers);
 * with ``valuation`` our own places and streets lead (see the prop).
 * "Pick on map" opens a modal with a much larger map for click-to-
 * place, used both when the user can't find their building in the
 * typeahead AND to fine-tune the pin after a typeahead pick. Map
 * clicks preserve the existing district / complex slug when one
 * was previously set - the user is nudging the pin, not declaring a
 * brand-new location.
 */
export default function LocationPicker({
  value,
  onChange,
  compact = false,
  fillPreview = false,
  valuation = false,
}: Props) {
  const t = useT();
  const { locale } = useLocale();
  const [query, setQuery] = useState(value?.display_label ?? "");
  // A value set from outside after mount (the calculator prefilled from
  // the chat's card) must show in the input too, not only on the map.
  // A user edit clears `value` first (see onChange), so this never
  // overwrites what someone is typing.
  useEffect(() => {
    if (value && value.display_label && query !== value.display_label) setQuery(value.display_label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.display_label]);
  const [complexes, setComplexes] = useState<ResidenceEntry[]>([]);
  const [addressResults, setAddressResults] = useState<PhotonFeature[]>([]);
  const [addressLoading, setAddressLoading] = useState(false);
  // The text Photon's results answer: until they land, the panel says
  // it is searching instead of "nothing found".
  const [photonFor, setPhotonFor] = useState("");
  // Our places and streets for the typed text (valuation mode), and the
  // text they answer: Photon waits until they are in.
  const [ours, setOurs] = useState<OurPlace[]>([]);
  const [oursFor, setOursFor] = useState("");
  const [oursLoading, setOursLoading] = useState(false);
  // A house address being placed (Photon) after its pick.
  const [resolving, setResolving] = useState(false);
  const houseAbortRef = useRef<AbortController | null>(null);
  // The point whose line (nearest endpoint) is in flight.
  const lineForRef = useRef<string>("");
  const [modalOpen, setModalOpen] = useState(false);
  // Portals need a DOM: render nothing on the server pass.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);
  // Drag to dismiss, phones only (owner, 2026-09-21: "the sheet to select
  // the location isn't closable by dragging on mobile"). The gesture
  // lives on the handle and the header so the map keeps its own pan and
  // zoom; only transform moves, and a drag past ~90 px closes.
  const dragFrom = useRef<number | null>(null);
  const [dragY, setDragY] = useState(0);
  const startDrag = (e: React.PointerEvent) => {
    if (window.innerWidth >= 640) return;
    dragFrom.current = e.clientY;
    setDragY(0);
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const moveDrag = (e: React.PointerEvent) => {
    if (dragFrom.current == null) return;
    setDragY(Math.max(0, e.clientY - dragFrom.current));
  };
  const endDrag = () => {
    if (dragFrom.current == null) return;
    dragFrom.current = null;
    setDragY((y) => {
      if (y > 90) setModalOpen(false);
      return 0;
    });
  };
  // ``pendingValue`` is the in-progress selection that lives only
  // inside the modal. We DON'T fan it back to the parent via onChange
  // on every click, because that would re-render the inline preview
  // map below the form (it's keyed on lat/lng to re-center). The user
  // explicitly asked for the preview to stay frozen until they hit
  // Done. So: open → snapshot value into pendingValue → clicks update
  // pendingValue only → Done commits → close.
  const [pendingValue, setPendingValue] = useState<SelectedLocation | null>(null);
  const [pinAddress, setPinAddress] = useState<string | null>(null);
  const [pinAddressLoading, setPinAddressLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const reverseAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    getComplexes().then(setComplexes).catch(() => setComplexes([]));
  }, []);

  // "Yusif Vəzir Çəmənzəminli 125": the street part goes to our search,
  // the number is kept for the house.
  const split = useMemo(() => splitHouseNumber(query), [query]);
  const text = valuation ? split.text : query.trim();

  // Diacritic-folded match over the name AND the catalog aliases, so a
  // plain-keyboard "qelebe residence" finds "Qələbə Residence" and
  // "28 Park Rezidens" finds "28 Park Residence".
  const residences = useMemo<ResidenceEntry[]>(() => {
    const full = matchResidences(query, complexes);
    if (full.length > 0 || !valuation || !split.number) return full;
    return matchResidences(split.text, complexes);
  }, [query, complexes, valuation, split]);

  const hasSelection = value !== null;

  // Our places and streets (valuation mode), 200 ms after the last
  // keystroke, in the user's language (the rows carry all three).
  useEffect(() => {
    if (!valuation) return;
    const q = text.trim();
    if (hasSelection || resolving || q.length < 2) {
      setOursLoading(false);
      if (q.length < 2) {
        setOurs([]);
        setOursFor("");
      }
      return;
    }
    let live = true;
    setOursLoading(true);
    const timer = window.setTimeout(() => {
      fetchAPI<{ suggestions?: OurPlace[] }>("/listings/suggest", { q, valuation: "1" })
        .then((d) => {
          if (!live) return;
          setOurs((d.suggestions ?? []).filter((r) => r.point && (r.kind === "location" || r.kind === "street")));
          setOursFor(q);
        })
        .catch(() => {
          if (!live) return;
          setOurs([]);
          setOursFor(q);
        })
        .finally(() => {
          if (live) setOursLoading(false);
        });
    }, 200);
    return () => {
      live = false;
      window.clearTimeout(timer);
    };
  }, [valuation, text, hasSelection, resolving]);

  const oursCurrent = valuation && oursFor === text.trim() && !oursLoading;
  // Photon: always in the other forms; in valuation mode only for an
  // exact address our streets do not cover, or when nothing of ours
  // matches, and only once ours are in for this text.
  const wantPhoton =
    !hasSelection &&
    !resolving &&
    query.trim().length >= 2 &&
    (!valuation || (oursCurrent && photonWanted({ number: split.number, ours, residences })));

  // Lock the body when the modal is open so background scroll doesn't
  // bleed through on mobile. Also wire Escape to close - matches the
  // dialog convention everywhere else in the app + recovers users who
  // open the modal by accident and don't see the X button at first.
  useEffect(() => {
    if (!modalOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setModalOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [modalOpen]);

  // Snapshot the current value into pendingValue when the modal opens.
  // Closing the modal without Done discards pendingValue, so the
  // parent's value (and therefore the inline preview) stay frozen.
  useEffect(() => {
    if (modalOpen) setPendingValue(value);
    // Intentionally omitting `value` from deps - we only snapshot on
    // open, not on every external value change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalOpen]);

  // Reverse-geocode the *pending* pin to a human address. Aborts any
  // in-flight request when the pin moves so stale results can't win.
  useEffect(() => {
    if (!modalOpen || !pendingValue) {
      setPinAddress(null);
      setPinAddressLoading(false);
      reverseAbortRef.current?.abort();
      return;
    }
    reverseAbortRef.current?.abort();
    const ctrl = new AbortController();
    reverseAbortRef.current = ctrl;
    setPinAddressLoading(true);
    // Photon first (street-level detail), our own taxonomy when it
    // fails OR returns nothing - a blocked request used to land in the
    // catch and leave the raw coordinates as the label.
    void (async () => {
      let addr: string | null = null;
      try {
        addr = await reverseGeocode(pendingValue.latitude, pendingValue.longitude, ctrl.signal);
      } catch {
        addr = null;
      }
      if (ctrl.signal.aborted) return;
      if (!addr) {
        addr = labelOfNearest(await nearestPlace(pendingValue.latitude, pendingValue.longitude));
      }
      if (ctrl.signal.aborted) return;
      setPinAddress(addr);
      setPinAddressLoading(false);
    })();
    return () => ctrl.abort();
  }, [modalOpen, pendingValue]);

  // Debounced Photon fetch (kicks 300 ms after the last keystroke).
  useEffect(() => {
    const q = query.trim();
    if (!wantPhoton) {
      setAddressResults([]);
      setAddressLoading(false);
      abortRef.current?.abort();
      return;
    }
    const timer = window.setTimeout(async () => {
      abortRef.current?.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setAddressLoading(true);
      try {
        const results = await searchAddresses(q, ctrl.signal);
        if (!ctrl.signal.aborted) setAddressResults(results);
      } catch {
        if (!ctrl.signal.aborted) setAddressResults([]);
      } finally {
        if (!ctrl.signal.aborted) {
          setAddressLoading(false);
          setPhotonFor(q);
        }
      }
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query, wantPhoton]);

  // Name a point the field did not pick itself (a pin, an address from
  // Photon, the calculator prefilled from a link): the nearest endpoint
  // gives the rayon and the place, and a coordinate label gets a name.
  // Once per point; a newer pick or an edit wins.
  const lineFor = (loc: SelectedLocation) => {
    const at = `${loc.latitude},${loc.longitude}`;
    lineForRef.current = at;
    void nearestPlace(loc.latitude, loc.longitude).then((n) => {
      if (!n || lineForRef.current !== at) return;
      lineForRef.current = "";
      const label = isCoordinateLabel(loc.display_label) ? labelOfNearest(n) ?? loc.display_label : loc.display_label;
      const next: SelectedLocation = { ...loc, display_label: label };
      if (valuation) {
        next.resolved = loc.resolved?.kind === "residence" ? loc.resolved : resolvedFromNearest(loc, n);
        if (loc.source === "pin") next.complex_slug = n.complex_slug ?? null;
      }
      onChange(next);
      if (label !== loc.display_label) setQuery(label);
    });
  };
  useEffect(() => {
    if (!valuation || !value || value.resolved || resolving) return;
    const at = `${value.latitude},${value.longitude}`;
    if (lineForRef.current === at) return;
    lineFor(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valuation, value?.latitude, value?.longitude, value?.resolved, resolving]);

  /** A pick from the panel: the input shows its label, the parent gets it. */
  const choose = (loc: SelectedLocation) => {
    houseAbortRef.current?.abort();
    houseAbortRef.current = null;
    setResolving(false);
    lineForRef.current = "";
    setQuery(loc.display_label);
    onChange(loc);
  };

  const handleComplexPick = (c: ResidenceEntry) => {
    choose({
      latitude: c.lat,
      longitude: c.lng,
      district: c.district || null,
      complex_korter_slug: c.korter_slug || null,
      display_label: c.name,
      source: "complex",
      complex_slug: c.cluster_id || null,
      street_id: null,
      place_id: null,
      resolved: { kind: "residence", name: c.name, area: c.district || null },
      number_missed: null,
    });
  };

  const labelOf = (row: OurPlace) => row.label[locale as "az" | "ru" | "en"] || row.label.az;

  const handlePlacePick = (row: OurPlace) => {
    const name = labelOf(row);
    choose({
      latitude: row.point.lat,
      longitude: row.point.lng,
      // bina's names are the same in every language: the place's name
      // is the area in our vocabulary.
      district: row.label.az,
      complex_korter_slug: null,
      display_label: name,
      source: "place",
      place_id: row.id,
      street_id: null,
      complex_slug: null,
      resolved: { kind: placeKind(row.type), name, area: row.type === "rayon" ? null : row.rayon ?? null },
      number_missed: null,
    });
  };

  const streetValue = (row: OurPlace): SelectedLocation => {
    const name = labelOf(row);
    return {
      latitude: row.point.lat,
      longitude: row.point.lng,
      district: row.district ?? null,
      complex_korter_slug: null,
      display_label: name,
      source: "street",
      street_id: Number(row.id),
      place_id: null,
      complex_slug: null,
      resolved: { kind: "street", name, area: row.place || row.district || null },
      number_missed: null,
    };
  };

  const handleStreetPick = (row: OurPlace) => choose(streetValue(row));

  // A house on one of our streets: Photon places the number; when it
  // cannot (or does not answer in 4 s), the street's middle with a note.
  const handleHousePick = async (row: OurPlace, number: string) => {
    const base = streetValue(row);
    const label = `${base.display_label} ${number}`;
    lineForRef.current = "";
    houseAbortRef.current?.abort();
    const ctrl = new AbortController();
    houseAbortRef.current = ctrl;
    setQuery(label);
    setResolving(true);
    onChange(null);
    const timer = window.setTimeout(() => ctrl.abort(), 4000);
    let house: { lat: number; lng: number } | null = null;
    try {
      const features = await searchAddresses(houseQuery(row.label.az, number), ctrl.signal, 10);
      house = houseFromPhoton(features, number, { name: row.label.az, point: row.point });
    } catch {
      house = null;
    }
    window.clearTimeout(timer);
    // An edit or another pick took over meanwhile.
    if (houseAbortRef.current !== ctrl) return;
    houseAbortRef.current = null;
    setResolving(false);
    if (house) {
      onChange({
        ...base,
        latitude: house.lat,
        longitude: house.lng,
        display_label: label,
        source: "address",
        resolved: { kind: "address", name: base.display_label, number, area: base.resolved?.area ?? null },
      });
    } else {
      onChange({ ...base, display_label: label, number_missed: number });
    }
  };

  const handleAddressPick = (f: PhotonFeature) => {
    const [lng, lat] = f.geometry.coordinates;
    const label = formatAddressLabel(f.properties);
    // A number typed that this result does not carry: it is the street,
    // and the field says the number was not found.
    const typed = valuation ? split.number : null;
    const missed = typed && !sameNumber(f.properties.housenumber, typed) ? typed : null;
    const isStreet = !f.properties.housenumber && (f.properties.type === "street" || f.properties.osm_key === "highway");
    const source: PlaceSource = missed || isStreet ? "street" : "address";
    const loc: SelectedLocation = {
      latitude: lat,
      longitude: lng,
      district: f.properties.district || null,
      complex_korter_slug: null,
      display_label: label,
      source,
      street_id: null,
      place_id: null,
      complex_slug: null,
      resolved: null,
      number_missed: missed,
    };
    choose(loc);
    if (valuation) lineFor(loc);
  };

  // Map-modal pick. Preserves the existing district / complex slug
  // when one was already set - the user is refining the pin position
  // for an already-named building, not declaring a brand new one. If
  // they had nothing picked, surfaces a lat/lng-shaped fallback label.
  // NOTE: writes to pendingValue, not onChange. The parent only sees
  // the update when the user hits Done.
  const handleMapPick = (lat: number, lng: number) => {
    const fallbackLabel = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
    setPendingValue((prev) => ({
      latitude: lat,
      longitude: lng,
      district: prev?.district ?? value?.district ?? null,
      complex_korter_slug:
        prev?.complex_korter_slug ?? value?.complex_korter_slug ?? null,
      display_label: prev?.display_label ?? value?.display_label ?? fallbackLabel,
    }));
  };

  const commitPendingAndClose = () => {
    if (!pendingValue) {
      onChange(null);
      setQuery("");
      setModalOpen(false);
      return;
    }
    // Promote the reverse-geocoded address into the visible label
    // (and the search bar) when one resolved during the modal
    // session. Falls back to the pending label - which may be the
    // preserved typeahead label or the lat/lng default - when Photon
    // returned nothing.
    const label = pinAddress ?? pendingValue.display_label;
    // Wherever it came from, a point set on the map is a pin: the
    // valuation snaps it to a residence only when it stands on one.
    const committed: SelectedLocation = {
      ...pendingValue,
      display_label: label,
      source: "pin",
      street_id: null,
      place_id: null,
      complex_slug: null,
      resolved: null,
      number_missed: null,
    };
    choose(committed);
    setModalOpen(false);
    // Done before the address resolved: the field would keep the raw
    // coordinates. Name the place from our own vocabulary and swap the
    // label in, unless the pin moved meanwhile; the valuation field also
    // gets its line (the residence the pin is on, or the rayon).
    if (valuation || isCoordinateLabel(label)) lineFor(committed);
  };

  const options = useMemo<FieldOption[]>(
    () =>
      valuation
        ? orderOptions({ query, number: split.number, ours, residences, photon: addressResults })
        : [
            ...residences.map((c): FieldOption => ({ kind: "residence", c })),
            ...addressResults.map((f): FieldOption => ({ kind: "photon", f })),
          ],
    [valuation, query, split, ours, residences, addressResults],
  );
  const searching =
    addressLoading ||
    (wantPhoton && photonFor !== query.trim()) ||
    (valuation && (oursLoading || !oursCurrent) && text.trim().length >= 2);
  const showDropdown = !!query && !hasSelection && !resolving;
  const showEmpty = !searching && options.length === 0;
  const groupLabel = (g: ReturnType<typeof optionGroup>) =>
    g === "house"
      ? t("sell.location.section.house")
      : g === "places"
        ? t("sell.location.section.places")
        : g === "streets"
          ? t("sell.location.section.streets")
          : g === "complexes"
            ? t("sell.location.section.complexes")
            : t("sell.location.section.addresses");

  const renderOption = (o: FieldOption) => {
    let Icon = MapPin;
    let title = "";
    let subtitle = "";
    let onPick: () => void;
    switch (o.kind) {
      case "address":
        Icon = House;
        title = `${labelOf(o.street)} ${o.number}`;
        subtitle = o.street.place || o.street.district || "";
        onPick = () => void handleHousePick(o.street, o.number);
        break;
      case "place": {
        Icon = o.row.type === "metro" ? TrainFront : MapPin;
        title = labelOf(o.row);
        const kind = o.row.sub?.[locale as "az" | "ru" | "en"] || o.row.sub?.az || "";
        subtitle = [kind, o.row.type !== "rayon" ? o.row.rayon : null].filter(Boolean).join(" · ");
        onPick = () => handlePlacePick(o.row);
        break;
      }
      case "street":
        Icon = Route;
        title = labelOf(o.row);
        subtitle = o.row.sub?.[locale as "az" | "ru" | "en"] || o.row.place || o.row.district || "";
        onPick = () => handleStreetPick(o.row);
        break;
      case "residence":
        Icon = Building2;
        title = o.c.name;
        subtitle = o.c.district;
        onPick = () => handleComplexPick(o.c);
        break;
      default:
        title = formatAddressLabel(o.f.properties);
        subtitle = addressSubtitle(o.f.properties);
        onPick = () => handleAddressPick(o.f);
    }
    return (
      <button
        type="button"
        onClick={onPick}
        className="w-full text-left px-4 py-2.5 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 flex items-start gap-2.5"
      >
        <Icon className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-1" aria-hidden />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-gray-900 truncate">{title}</div>
          {subtitle && <div className="text-[11px] text-gray-500 mt-0.5 truncate">{subtitle}</div>}
        </div>
      </button>
    );
  };
  const optionKey = (o: FieldOption, i: number) =>
    o.kind === "address"
      ? `a-${o.street.id}-${o.number}`
      : o.kind === "place" || o.kind === "street"
        ? `${o.kind}-${o.row.id}`
        : o.kind === "residence"
          ? `r-${o.c.cluster_id || o.c.korter_slug || o.c.name}`
          : `p-${o.f.geometry.coordinates.join(",")}-${i}`;

  // Modal map centre/zoom/marker derive from the *initial* snapshot
  // (i.e. the value at the moment the modal opened). The modal map
  // does NOT remount on subsequent pin moves - that's why we read
  // from value, not pendingValue, here. Centre on Baku at city zoom
  // when nothing was picked yet.
  const modalCenter: [number, number] = value
    ? [value.latitude, value.longitude]
    : BAKU_CENTER;
  const modalZoom = value ? 16 : 12;
  const modalMarker: [number, number] | null = value
    ? [value.latitude, value.longitude]
    : null;

  return (
    <div className="space-y-2">
      <label className="mb-1 block text-[11px] font-semibold text-gray-500">
        {t("sell.location.label")}
      </label>

      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            // An edit cancels a house still being placed.
            if (houseAbortRef.current) {
              houseAbortRef.current.abort();
              houseAbortRef.current = null;
              setResolving(false);
            }
            if (value && e.target.value !== value.display_label) {
              lineForRef.current = "";
              onChange(null);
            }
          }}
          placeholder={valuation ? t("sell.location.placeholder.places") : t("sell.location.complex.placeholder")}
          className={`w-full pl-9 ${compact ? "pr-11" : "pr-3"} py-2.5 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent outline-none`}
        />
        {compact && (
          // Map button lives inside the input (right edge) so the tall
          // placeholder below can be dropped - opens the same modal.
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => setModalOpen(true)}
            aria-label={t("sell.location.open_map")}
            title={t("sell.location.open_map")}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          >
            <MapPin className="w-4 h-4" />
          </button>
        )}
        {showDropdown && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-[22rem] overflow-y-auto">
            {options.map((o, i) => {
              const g = optionGroup(o);
              const head = i === 0 || optionGroup(options[i - 1]) !== g;
              return (
                <Fragment key={optionKey(o, i)}>
                  {head && (
                    <div className="px-4 pt-2.5 pb-1 text-[10px] font-semibold text-gray-400">
                      {groupLabel(g)}
                    </div>
                  )}
                  {renderOption(o)}
                </Fragment>
              );
            })}
            {searching && (
              <div className="flex items-center gap-1.5 px-4 py-2.5 text-[11px] text-gray-400">
                <Loader2 className="w-3 h-3 animate-spin" aria-hidden />
                {t("sell.location.searching")}
              </div>
            )}
            {showEmpty && (
              <div className="px-4 py-3 flex items-start gap-2.5">
                <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-1" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs text-gray-500">
                    {valuation ? t("sell.location.notfound") : t("sell.location.complex.notfound")}
                  </div>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      // Stops the input blur that would close the
                      // dropdown before the click registers on
                      // some browsers.
                      e.preventDefault();
                    }}
                    onClick={() => setModalOpen(true)}
                    className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-gray-900 underline-offset-2 underline decoration-gray-300 hover:decoration-gray-900"
                  >
                    {t("sell.location.notlisted")}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* What the valuation will use, quietly under the input: the street
          and its district, the station with its rayon, the residence,
          the rayon and nearest place of a pin (owner, 2026-10-26: the
          result said Nəsimi for a street in Nərimanov). A house number
          the map does not know is said, not silently dropped. */}
      {valuation && (resolving || value?.resolved || value?.number_missed) && (
        <div className="space-y-1" aria-live="polite">
          {resolving ? (
            <p className="flex items-center gap-1.5 text-[11px] leading-snug text-gray-500">
              <Loader2 className="h-3 w-3 shrink-0 animate-spin" aria-hidden />
              {t("sell.location.finding_house")}
            </p>
          ) : value?.resolved ? (
            <p className="flex items-start gap-1.5 text-[11px] leading-snug text-gray-500" title={t("sell.location.line.sr")}>
              <MapPin className="mt-px h-3 w-3 shrink-0 text-gray-400" aria-hidden />
              <span className="sr-only">{t("sell.location.line.sr")}: </span>
              <span className="min-w-0">{resolvedText(value.resolved, t)}</span>
            </p>
          ) : null}
          {!resolving && value?.number_missed && (
            <p className="text-[11px] leading-snug text-amber-700">
              {t("sell.location.number_missed").replace("{n}", value.number_missed)}{" "}
              <button
                type="button"
                onClick={() => setModalOpen(true)}
                className="font-semibold underline underline-offset-2 hover:text-amber-800"
              >
                {t("sell.location.number_missed.action")}
              </button>
            </p>
          )}
        </div>
      )}

      {/* Below the search input we either show the read-only preview
          map (with an Edit button overlay that opens the modal) when
          a location is picked, OR a quiet dashed "Pick on map" CTA that
          reserves the same vertical space when nothing's picked yet.
          The previous label+district pill chip is gone - the selected
          location's label lives in the search input itself now.
          In compact mode this whole block is dropped - the map button
          inside the input above is the only "pick on map" affordance. */}
      {/* Compact mode keeps the small field the forms asked for, but a
          picked point still has to be checkable: a static Mapbox image
          (one request, no GL bundle) shows exactly where the pin sits
          and opens the full picker when tapped (owner, 2026-09-22: "we
          don't present the location itself on the map, so we probably
          need to return the map somehow to see exactly"). */}
      {compact && (fillPreview || (hasSelection && value && MAPBOX_TOKEN)) && (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className={`group relative block w-full overflow-hidden rounded-xl border ${
            hasSelection && value && MAPBOX_TOKEN
              ? "border-gray-200"
              : "border-dashed border-gray-300 bg-gray-50 hover:bg-gray-100 transition-colors"
          } ${fillPreview ? "h-36 sm:h-44 lg:h-48" : "h-24"}`}
          aria-label={
            hasSelection && value
              ? t("sell.location.adjust_map")
              : t("sell.location.open_map")
          }
        >
          {hasSelection && value && MAPBOX_TOKEN ? (
            <>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={`https://api.mapbox.com/styles/v1/mapbox/streets-v12/static/pin-s+4f46e5(${value.longitude.toFixed(5)},${value.latitude.toFixed(5)})/${value.longitude.toFixed(5)},${value.latitude.toFixed(5)},14.5/640x280@2x?access_token=${MAPBOX_TOKEN}&attribution=false&logo=false`}
                alt=""
                aria-hidden
                loading="lazy"
                className="h-full w-full object-cover"
              />
              {/* The whole image opens the picker, so a permanent
                  "Dəyiş" chip was a second button for the same thing
                  (owner, 2026-09-22: "there is a change button on the
                  map preview, but you can click wherever you want on
                  the map"). It only surfaces on hover, where a pointer
                  exists to be told what the click does; on touch the
                  pin inside the field is the explicit affordance. */}
              <span className="pointer-events-none absolute inset-x-0 bottom-0 hidden justify-center bg-gradient-to-t from-gray-900/80 via-gray-900/40 to-transparent px-3 pb-2 pt-8 opacity-0 transition-opacity group-hover:opacity-100 sm:flex">
                <span className="text-[11px] font-semibold text-white">
                  {t("sell.location.preview.hint")}
                </span>
              </span>
            </>
          ) : (
            // The empty state holds the preview's exact height, so the
            // map fades into a box that was already there instead of
            // pushing the form down the moment something is picked
            // (owner, 2026-09-22: "it shouldn't just suddenly appear").
            <span className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-gray-400">
              <MapPin className="h-5 w-5" />
              <span className="text-xs font-medium text-gray-500">
                {t("sell.location.open_map")}
              </span>
            </span>
          )}
        </button>
      )}

      {!compact &&
        (hasSelection && value ? (
        <div className="relative">
          <LeafletMap
                className="h-56 rounded-xl overflow-hidden border border-gray-200"
            key={`${value.latitude},${value.longitude}`}
            initialCenter={[value.latitude, value.longitude]}
            initialZoom={16}
            initialMarker={[value.latitude, value.longitude]}
          />
          {/* Edit button overlay - top-right corner, opens the modal
              for click-to-place. The preview itself is read-only
              (no onPickLocation) so casual clicks on the map don't
              accidentally move the pin. */}
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            className="absolute top-2 right-2 inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 bg-white text-gray-900 rounded-lg text-[11px] font-semibold shadow-md hover:border-gray-300 transition-colors"
          >
            <Pencil className="w-3 h-3" />
            {t("sell.location.adjust_map")}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          aria-hidden="false"
          className="w-full h-56 rounded-xl border border-dashed border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100 flex flex-col items-center justify-center gap-2 px-4 text-center transition-colors"
        >
          <MapPin className="w-5 h-5 text-gray-500" />
          <p className="text-xs text-gray-700 max-w-[16rem] leading-relaxed font-medium">
            {t("sell.location.map.placeholder")}
          </p>
          <span className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-gray-900 underline underline-offset-2">
            {t("sell.location.open_map")} →
          </span>
        </button>
        ))}

      {/* Modal map - bigger surface for accurate pin placement. Click
          on the map updates the selection (preserving the district +
          complex_korter_slug if the user had picked from the typeahead
          first - they're refining the pin, not redefining the
          building). Click outside the modal or on the close button
          dismisses; the selection persists. */}
      {modalOpen && mounted && createPortal(
        /* A sheet on phones, a centred dialog from sm up (owner,
           2026-09-21: "the location selector on mobile is not suitable,
           I think you should go with this sheet, probably with a button
           at the bottom"). Same DOM either way, so the map does not
           remount when the viewport changes.

           Rendered into <body>: z-[2000] only beats the sticky header
           when nothing between them opens a stacking context, and on
           the forms that host this picker something always did - the
           sheet slid UNDER the avatar and the Pro badge (owner,
           2026-09-22). In a portal there is no ancestor left to trap
           it, and the backdrop takes every pointer event. */
        <div
          className="fixed inset-0 z-[2000] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-6"
          onClick={() => setModalOpen(false)}
        >
          <div
            className="flex max-h-[92vh] w-full max-w-3xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:max-h-[90vh] sm:rounded-2xl"
            style={{
              transform: dragY ? `translateY(${dragY}px)` : undefined,
              transition: dragFrom.current == null ? "transform 200ms ease-out" : "none",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Handle + header are the drag surface. */}
            <div
              className="touch-none"
              onPointerDown={startDrag}
              onPointerMove={moveDrag}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <div className="flex justify-center pt-2 sm:hidden">
                <span className="h-1 w-10 rounded-full bg-gray-300" />
              </div>
              <div className="flex items-start justify-between gap-3 px-4 py-3 sm:px-5">
              <div className="min-w-0 flex-1">
                <h2 className="text-sm font-bold text-gray-900">
                  {t("sell.location.modal.title")}
                </h2>
                <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
                  {t("sell.location.modal.hint")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                aria-label={t("a11y.close")}
                className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 shrink-0"
              >
                <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            {/* Reserve the map slot's vertical space BEFORE the
                dynamic-import resolves. Without min-h here, the
                modal's max-h-[90vh] outer card shrinks to fit the
                tiny "Loading map…" placeholder, then snaps to full
                size once mapbox-gl mounts. min-h matches the
                LeafletMap's own ``h-[60vh] sm:h-[520px]`` so the
                container's height is identical loaded vs loading. */}
            <div className="relative min-h-[48vh] flex-1 bg-gray-50 sm:min-h-[520px]">
              {/* No ``key`` here on purpose - we DON'T want the map to
                  remount on every pin move (that snaps the viewport
                  back to the modalZoom default and undoes any pan/
                  zoom the user did to position the pin precisely).
                  ``initialViewState`` only applies on the first mount
                  per modal-open cycle; subsequent clicks just move
                  the marker. The whole map unmounts when the modal
                  closes and re-centers fresh on the next open. */}
              <LeafletMap
                className="h-[48vh] sm:h-[520px]"
                onPickLocation={handleMapPick}
                initialCenter={modalCenter}
                initialZoom={modalZoom}
                initialMarker={modalMarker}
              />
            </div>
            <div className="flex flex-col gap-2 border-t border-gray-200 bg-gray-50 px-4 py-3 pb-[calc(0.75rem+3.5rem+env(safe-area-inset-bottom))] sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:px-5 sm:pb-3 sm:pb-3">
              <div className="flex min-w-0 flex-1 items-center gap-1.5 text-[11px] text-gray-600">
                {!pendingValue ? (
                  <span className="truncate">
                    {t("sell.location.modal.no_pick")}
                  </span>
                ) : pinAddressLoading ? (
                  <>
                    <Loader2 className="w-3 h-3 animate-spin shrink-0" />
                    <span className="truncate">
                      {t("sell.location.modal.geocoding")}
                    </span>
                  </>
                ) : (
                  <>
                    <MapPin className="w-3 h-3 shrink-0 text-gray-400" />
                    <span className="truncate">
                      {pinAddress ?? pendingValue.display_label}
                    </span>
                  </>
                )}
              </div>
              <button
                type="button"
                onClick={commitPendingAndClose}
                disabled={!pendingValue}
                className="w-full shrink-0 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto sm:py-2 sm:text-xs"
              >
                {t("sell.location.modal.done")}
              </button>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
}
